// EmailDetail component
// Displays full email content in a modal or panel

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { XMarkIcon, PaperClipIcon, LinkIcon, BuildingOfficeIcon, UserIcon, TrashIcon } from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import { useState } from 'react'

type EmailDetailProps = {
  emailId: string
  onClose: () => void
  onUpdated?: () => void
}

type EmailDetail = {
  id: string
  subject: string
  fromEmail: string
  fromName: string | null
  toEmails: string[]
  ccEmails: string[]
  sentAt: string | null
  bodyText: string | null
  bodyHtml: string | null
  hasAttachments: boolean
  note: string | null
  accountId: string | null
  contactId: string | null
  leadId: string | null
  account: { id: string; name: string } | null
  contact: { id: string; firstName: string | null; lastName: string } | null
  lead: { id: string; firstName: string | null; lastName: string; company: string } | null
  attachments: Array<{
    id: string
    filename: string
    mimeType: string | null
    size: number | null
  }>
}

type SearchResult = {
  accounts: Array<{ id: string; name: string }>
  contacts: Array<{ id: string; name: string; accountId: string; accountName: string }>
  leads: Array<{ id: string; name: string; company: string }>
}

async function fetchEmail(id: string): Promise<EmailDetail> {
  const response = await fetch(`/api/emails/${id}`, { credentials: 'include' })
  if (!response.ok) throw new Error('Failed to fetch email')
  return response.json()
}

async function searchEntities(query: string): Promise<SearchResult> {
  const response = await fetch(`/api/emails/search/entities?q=${encodeURIComponent(query)}`, {
    credentials: 'include',
  })
  if (!response.ok) throw new Error('Failed to search')
  return response.json()
}

async function linkEmail(
  emailId: string,
  data: { accountId?: string | null; contactId?: string | null; leadId?: string | null }
): Promise<void> {
  const response = await fetch(`/api/emails/${emailId}/link`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    credentials: 'include',
  })
  if (!response.ok) throw new Error('Failed to link email')
}

async function deleteEmail(emailId: string): Promise<void> {
  const response = await fetch(`/api/emails/${emailId}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!response.ok) throw new Error('Failed to delete email')
}

export function EmailDetail({ emailId, onClose, onUpdated }: EmailDetailProps) {
  const queryClient = useQueryClient()
  const [showLinkUI, setShowLinkUI] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const { data: email, isLoading, error } = useQuery({
    queryKey: ['email', emailId],
    queryFn: () => fetchEmail(emailId),
  })

  const { data: searchResults } = useQuery({
    queryKey: ['entity-search', searchQuery],
    queryFn: () => searchEntities(searchQuery),
    enabled: searchQuery.length >= 2,
  })

  const linkMutation = useMutation({
    mutationFn: (data: { accountId?: string | null; contactId?: string | null; leadId?: string | null }) =>
      linkEmail(emailId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', emailId] })
      queryClient.invalidateQueries({ queryKey: ['emails'] })
      setShowLinkUI(false)
      setSearchQuery('')
      onUpdated?.()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteEmail(emailId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] })
      onUpdated?.()
      onClose()
    },
  })

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this email?')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !email) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 text-center">
          <p className="text-red-600">Failed to load email</p>
          <button onClick={onClose} className="mt-4 text-gray-600 hover:text-gray-900">
            Close
          </button>
        </div>
      </div>
    )
  }

  const formatSize = (bytes: number | null) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b">
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-lg font-semibold text-gray-900 truncate">{email.subject}</h2>
            <div className="mt-1 text-sm text-gray-500">
              From: {email.fromName ? `${email.fromName} <${email.fromEmail}>` : email.fromEmail}
            </div>
            {email.toEmails.length > 0 && (
              <div className="text-sm text-gray-500">
                To: {email.toEmails.join(', ')}
              </div>
            )}
            {email.ccEmails.length > 0 && (
              <div className="text-sm text-gray-500">
                Cc: {email.ccEmails.join(', ')}
              </div>
            )}
            {email.sentAt && (
              <div className="text-sm text-gray-400">
                {format(new Date(email.sentAt), 'PPpp')}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="p-1 text-gray-400 hover:text-red-600 rounded disabled:opacity-50"
              title="Delete email"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Linked entity */}
        <div className="px-4 py-2 bg-gray-50 border-b flex items-center gap-4">
          <span className="text-sm text-gray-500">Linked to:</span>
          {email.account && (
            <span className="inline-flex items-center gap-1 text-sm text-blue-600">
              <BuildingOfficeIcon className="h-4 w-4" />
              {email.account.name}
            </span>
          )}
          {email.contact && (
            <span className="inline-flex items-center gap-1 text-sm text-purple-600">
              <UserIcon className="h-4 w-4" />
              {email.contact.firstName} {email.contact.lastName}
            </span>
          )}
          {email.lead && !email.account && (
            <span className="inline-flex items-center gap-1 text-sm text-orange-600">
              <UserIcon className="h-4 w-4" />
              {email.lead.firstName} {email.lead.lastName}
            </span>
          )}
          {!email.account && !email.contact && !email.lead && (
            <span className="text-sm text-gray-400 italic">None</span>
          )}
          <button
            onClick={() => setShowLinkUI(!showLinkUI)}
            className="ml-auto text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
          >
            <LinkIcon className="h-4 w-4" />
            {showLinkUI ? 'Cancel' : 'Link to...'}
          </button>
        </div>

        {/* Link UI */}
        {showLinkUI && (
          <div className="px-4 py-3 bg-gray-50 border-b">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search accounts, contacts, or leads..."
              className="w-full px-3 py-2 border rounded-md text-sm"
              autoFocus
            />
            {searchResults && (searchResults.accounts.length > 0 || searchResults.contacts.length > 0 || searchResults.leads.length > 0) && (
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                {searchResults.accounts.map((account) => (
                  <button
                    key={`account-${account.id}`}
                    onClick={() => linkMutation.mutate({ accountId: account.id })}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <BuildingOfficeIcon className="h-4 w-4 text-blue-500" />
                    <span>{account.name}</span>
                    <span className="text-gray-400 text-xs">Account</span>
                  </button>
                ))}
                {searchResults.contacts.map((contact) => (
                  <button
                    key={`contact-${contact.id}`}
                    onClick={() => linkMutation.mutate({ accountId: contact.accountId, contactId: contact.id })}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <UserIcon className="h-4 w-4 text-purple-500" />
                    <span>{contact.name}</span>
                    <span className="text-gray-400 text-xs">at {contact.accountName}</span>
                  </button>
                ))}
                {searchResults.leads.map((lead) => (
                  <button
                    key={`lead-${lead.id}`}
                    onClick={() => linkMutation.mutate({ leadId: lead.id })}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <UserIcon className="h-4 w-4 text-orange-500" />
                    <span>{lead.name}</span>
                    <span className="text-gray-400 text-xs">{lead.company}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Note */}
        {email.note && (
          <div className="px-4 py-3 bg-amber-50 border-b">
            <div className="text-xs font-medium text-amber-700 mb-1">Note</div>
            <p className="text-sm text-amber-900">{email.note}</p>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {email.bodyHtml ? (
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: email.bodyHtml }}
            />
          ) : email.bodyText ? (
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
              {email.bodyText}
            </pre>
          ) : (
            <p className="text-gray-400 italic">No content</p>
          )}
        </div>

        {/* Attachments */}
        {email.attachments.length > 0 && (
          <div className="px-4 py-3 border-t bg-gray-50">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <PaperClipIcon className="h-4 w-4" />
              <span>{email.attachments.length} attachment{email.attachments.length > 1 ? 's' : ''}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {email.attachments.map((att) => (
                <div
                  key={att.id}
                  className="px-3 py-1 bg-white border rounded text-sm text-gray-700"
                >
                  {att.filename}
                  {att.size && <span className="text-gray-400 ml-1">({formatSize(att.size)})</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
